import { Grid } from '@mui/material';
import { ReactNode } from 'react';
import { FieldConfiguration, PermitFieldConfiguration } from 'types/Permit';
import AuturaSwitchWithOption from './AuturaSwitchWithOption';
import { useTranslation } from 'react-i18next';

export interface PermitFieldConfigProps {
  fieldConfig: PermitFieldConfiguration;
  onChange: (config: PermitFieldConfiguration) => void;
  disabled?: boolean;
}

const PermitFieldConfig = ({
  fieldConfig,
  onChange,
  disabled = false,
}: PermitFieldConfigProps): ReactNode => {
  const { t } = useTranslation();
  const handleFieldChange = (
    fieldKey: keyof PermitFieldConfiguration,
    property: keyof FieldConfiguration,
    value: boolean,
  ) => {
    const updatedConfig = {
      ...fieldConfig,
      [fieldKey]: {
        ...fieldConfig[fieldKey],
        [property]: value,
      },
    };
    onChange(updatedConfig);
  };

  const getFieldValue = (
    fieldKey: keyof PermitFieldConfiguration,
    property: keyof FieldConfiguration,
  ): boolean => {
    return fieldConfig[fieldKey]?.[property] || false;
  };

  return (
    <Grid container spacing={1.5} sx={{ width: '100%' }}>
      <Grid size={{ xs: 12, md: 6, lg: 3 }}>
        <AuturaSwitchWithOption
          name='vin'
          label={t('permit_type.data.fieldConfig.vin', {
            ns: 'entities',
          })}
          switchValue={getFieldValue('vin', 'enabled')}
          setSwitchValue={(value) => handleFieldChange('vin', 'enabled', value)}
          checkboxValue={getFieldValue('vin', 'required')}
          setCheckboxValue={(value) =>
            handleFieldChange('vin', 'required', value)
          }
          disabled={disabled}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6, lg: 3 }}>
        <AuturaSwitchWithOption
          name='color'
          label={t('permit_type.data.fieldConfig.color', {
            ns: 'entities',
          })}
          switchValue={getFieldValue('color', 'enabled')}
          setSwitchValue={(value) =>
            handleFieldChange('color', 'enabled', value)
          }
          checkboxValue={getFieldValue('color', 'required')}
          setCheckboxValue={(value) =>
            handleFieldChange('color', 'required', value)
          }
          disabled={disabled}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6, lg: 3 }}>
        <AuturaSwitchWithOption
          name='makeModel'
          label={t('permit_type.data.fieldConfig.makeModel', {
            ns: 'entities',
          })}
          switchValue={getFieldValue('makeModel', 'enabled')}
          setSwitchValue={(value) =>
            handleFieldChange('makeModel', 'enabled', value)
          }
          checkboxValue={getFieldValue('makeModel', 'required')}
          setCheckboxValue={(value) =>
            handleFieldChange('makeModel', 'required', value)
          }
          disabled={disabled}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6, lg: 3 }}>
        <AuturaSwitchWithOption
          name='driverLicense'
          label={t('permit_type.data.fieldConfig.driverLicense', {
            ns: 'entities',
          })}
          switchValue={getFieldValue('driverLicense', 'enabled')}
          setSwitchValue={(value) =>
            handleFieldChange('driverLicense', 'enabled', value)
          }
          checkboxValue={getFieldValue('driverLicense', 'required')}
          setCheckboxValue={(value) =>
            handleFieldChange('driverLicense', 'required', value)
          }
          disabled={disabled}
        />
      </Grid>
    </Grid>
  );
};

export default PermitFieldConfig;
